import React, { useEffect, useState } from "react";
import { Card, DatePicker, Table, Alert, Collapse, Typography, Tag } from "antd";
import dayjs from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
import isoWeek from "dayjs/plugin/isoWeek";
import Holidays from "date-holidays";
import { getMediaPlansByWeek } from "../../../core/api/mediaplan";

// Extend dayjs with week plugins
dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);

const { Panel } = Collapse;
const { Text } = Typography;
const hd = new Holidays("US");

const MailDateCard = ({ mailDate, brand_id }) => {
  const [campaignsInWeek, setCampaignsInWeek] = useState([]);
  const [isHoliday, setIsHoliday] = useState(false);
  const [isSunday, setIsSunday] = useState(false);
  const [loading, setLoading] = useState(false);
  const [holidayName, setHolidayName] = useState("");
  const [weekRange, setWeekRange] = useState({ start: null, end: null });

  useEffect(() => {
    if (!mailDate || !brand_id) return;

    const date = dayjs(mailDate);
    setIsSunday(date.day() === 0);

    const holiday = hd.isHoliday(new Date(mailDate));
    setIsHoliday(Boolean(holiday));
    if (holiday && holiday.length > 0) {
      setHolidayName(holiday[0].name);
    } else {
      setHolidayName("");
    }

    // Calculate the start and end of the week containing the selected date
    // Using Monday as the first day of the week (ISO week)
    const weekStart = date.startOf("isoWeek").format("YYYY-MM-DD");
    const weekEnd = date.endOf("isoWeek").format("YYYY-MM-DD");

    setWeekRange({
      start: weekStart,
      end: weekEnd,
      weekNumber: date.isoWeek(),
      year: date.year(),
    });

    const fetchCampaignsInWeek = async () => {
      setLoading(true);
      try {
        console.log("Fetching campaigns for week:", weekStart, "to", weekEnd);
        const response = await getMediaPlansByWeek(weekStart, weekEnd, brand_id);
        // Sort campaigns by mail date
        const sortedCampaigns = (response.data || []).sort((a, b) => {
          return dayjs(a.date).diff(dayjs(b.date));
        });
        setCampaignsInWeek(sortedCampaigns);
      } catch (error) {
        console.error("Error fetching campaigns for week:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCampaignsInWeek();
  }, [mailDate, brand_id]);

  const columns = [
    {
      title: "Mail Date",
      dataIndex: "date",
      key: "date",
      render: (date) => dayjs(date).format("YYYY-MM-DD"),
      sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
      defaultSortOrder: "ascend",
    },
    {
      title: "Product",
      key: "product",
      render: (record) => {
        const products = record.offer_details?.product_variations_detail || [];
        return products.map((p) => p.name).join(", ");
      },
    },
    {
      title: "Offer Code",
      key: "offer_code",
      render: (record) => record.offer_details?.code_offer || "-",
    },
    {
      title: "Campaign Code",
      dataIndex: "campaign_code",
      key: "campaign_code",
    },
    {
      title: "Target Mail Qty",
      dataIndex: "target_mail_qty",
      key: "target_mail_qty",
      render: (value) => Number(value).toLocaleString(),
    },
    {
      title: "Rate of Response",
      dataIndex: "target_rate_of_response",
      key: "target_rate_of_response",
      render: (value) => `${value}%`,
    },
    {
      title: "Printer",
      key: "printer",
      render: (record) => record.printer?.name || "-",
    },
    {
      title: "Status",
      key: "status",
      render: (record) => {
        const statusName = record.status?.name || "Unknown";
        let color = "default";

        if (statusName === "Confirmed") color = "green";
        else if (statusName === "Draft") color = "blue";
        else if (statusName === "Cancelled") color = "red";

        return <Tag color={color}>{statusName}</Tag>;
      },
    },
    {
      title: "Type",
      key: "type",
      render: (record) => {
        const typeOfOffer = record.offer_details?.type_of_offer?.type_of_offer || "-";
        const formOfOffer = record.offer_details?.form_of_offer?.form_of_offer || "-";
        return `${typeOfOffer} / ${formOfOffer}`;
      },
    },
  ];

  return (
    <div>
      <>
        {(isSunday || isHoliday) && (
          <Alert
            message={
              isSunday
                ? "Warning: Selected date is a Sunday"
                : `Warning: Selected date is a US holiday (${holidayName})`
            }
            type="warning"
            showIcon
            className="mb-1"
          />
        )}

        <Collapse defaultActiveKey={["1"]}>
          <Panel
            header={
              <div className="d-flex justify-content-between align-items-center">
                <span>
                  Week {weekRange.weekNumber} ({weekRange.year}): {weekRange.start} to{" "}
                  {weekRange.end}
                </span>
                {campaignsInWeek.length > 0 && (
                  <Tag color="blue">{campaignsInWeek.length} campaign(s)</Tag>
                )}
              </div>
            }
            key="1">
            <Table
              columns={columns}
              dataSource={campaignsInWeek}
              rowKey="id"
              size="small"
              className="small-font"
              pagination={false}
              loading={loading}
              locale={{ emptyText: "No campaigns scheduled for this week" }}
              scroll={{ x: "max-content" }}
            />

            {campaignsInWeek.length > 0 && (
              <div className="mt-1">
                <div className="d-flex justify-content-between">
                  <Text strong>
                    Total Mail Quantity:{" "}
                    {campaignsInWeek
                      .reduce((sum, campaign) => sum + Number(campaign.target_mail_qty), 0)
                      .toLocaleString()}
                  </Text>
                  <Text>
                    Expected Responses:{" "}
                    {campaignsInWeek
                      .reduce((sum, campaign) => {
                        const qty = Number(campaign.target_mail_qty);
                        const rate = Number(campaign.target_rate_of_response);
                        return sum + Math.round(qty * (rate / 100));
                      }, 0)
                      .toLocaleString()}
                  </Text>
                </div>
              </div>
            )}
          </Panel>
        </Collapse>
      </>
    </div>
  );
};

export default MailDateCard;
